import "../App.css";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Warning from "../components/Warning";
import React, { useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";

const PRIVACY_SECTIONS = [
  {
    id: "overview",
    title: "1. Overview & Processing",
    content: (
      <>
        <p>
          Welcome to the Privacy Policy for <strong>JustVIN</strong> (package name: <code>com.hungryCaterpillar.justvin</code>). JustVIN is designed with user privacy as a foundational principle.
        </p>
        <p>
          <strong>All data processing occurs directly on your device.</strong> There is no remote server, no account sign-up system, and no cloud storage involved in the operation of this application.
        </p>
      </>
    ),
  },
  {
    id: "data-collected",
    title: "2. Data Collected",
    content: (
      <>
        <p>JustVIN processes the following specific types of data to provide its services:</p>
        <ul style={{ textAlign: "left", paddingLeft: "20px", color: "var(--homeMid)" }}>
          <li style={{ marginBottom: "12px" }}>
            <strong>VIN scans (camera):</strong> Decoded vehicle identification numbers scanned via your device's camera are processed entirely on-device using Google ML Kit. The image itself is never saved, stored, or transmitted off your device.
          </li>
          <li style={{ marginBottom: "12px" }}>
            <strong>Scan history:</strong> Your recent scans are stored locally on your device in a Room database. This history is for your personal convenience and never leaves the device.
          </li>
          <li style={{ marginBottom: "12px" }}>
            <strong>VIN lookup:</strong> When you perform a VIN lookup, the VIN is sent directly to the National Highway Traffic Safety Administration (NHTSA) vPIC API (<a href="https://vpic.nhtsa.dot.gov/api/" target="_blank" rel="noopener noreferrer" className="privacy-highlight-link">vpic.nhtsa.dot.gov/api/</a>). <strong>No personal information</strong> is attached to this lookup request.
          </li>
          <li style={{ marginBottom: "12px" }}>
            <strong>Recall lookup:</strong> When looking up recalls, the app opens an external web browser page with the vehicle's VIN appended to the URL. The JustVIN application itself does not transmit any separate data for this action.
          </li>
          <li style={{ marginBottom: "12px" }}>
            <strong>Purchase data:</strong> Optional in-app tips are processed via Google Play Billing. All billing tokens are handled entirely by Google Play Services; the app itself does not see, store, or receive your personal payment or account details.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "data-sharing",
    title: "3. Data Sharing",
    content: (
      <>
        <p>
          We do not sell, rent, trade, or share any user data with third parties.
        </p>
        <p>
          The only external transmission occurs when requesting vehicle specifications from the NHTSA API, which receives only the raw VIN and no user-identifying data.
        </p>
      </>
    ),
  },
  {
    id: "legal-basis",
    title: "4. Legal Basis (GDPR)",
    content: (
      <>
        <p>
          For users residing in the European Economic Area (EEA) and the United Kingdom, VIN data is processed based on <strong>legitimate interest</strong>.
        </p>
        <p>
          Decoding the VIN and displaying vehicle specs or recall links constitutes the core functionality of the app. No data is retained by us beyond your active app session.
        </p>
      </>
    ),
  },
  {
    id: "data-controller",
    title: "5. Data Controller",
    content: (
      <>
        <p>
          Jack Frederick, developer of JustVIN, is the data controller for any information processed by the JustVIN app.
        </p>
        <p>
          If you have questions, comments, or requests regarding this Privacy Policy, please contact:
        </p>
        <p style={{ marginTop: "12px" }}>
          <a id="justvin-email-link" className="privacy-highlight-link" href="mailto:jack@csfrederick.com">
            jack@csfrederick.com
          </a>
        </p>
      </>
    ),
  },
  {
    id: "transfers",
    title: "6. International Data Transfers",
    content: (
      <>
        <p>
          The NHTSA vPIC API is hosted in the United States. When you query a VIN, that VIN (without any personal identifier) is transferred to United States servers. No other cross-border data transfers occur.
        </p>
      </>
    ),
  },
  {
    id: "user-rights",
    title: "7. Your Rights (GDPR)",
    content: (
      <>
        <p>
          Under the General Data Protection Regulation (GDPR), users in the European Union have the right to access, correct, restrict, erase, and export their data.
        </p>
        <p>
          Because JustVIN stores all data locally on your device and does not have an external server database, these rights are inherently satisfied through your own device-level control.
        </p>
        <p>
          You can exercise your right to erasure by uninstalling the application or clearing the app storage. You may also lodge a complaint with your local data protection authority if you believe your data privacy rights have been violated.
        </p>
      </>
    ),
  },
  {
    id: "automated-decisions",
    title: "8. Automated Decisions & Profiling",
    content: (
      <>
        <p>
          JustVIN does not use automated decision-making, profiling, or algorithmic evaluation that could legally affect you.
        </p>
      </>
    ),
  },
  {
    id: "data-deletion",
    title: "9. Data Deletion",
    content: (
      <>
        <p>
          Uninstalling the JustVIN application from your mobile device permanently deletes all local Room database history and cached VIN data. Since no user accounts or online profiles exist, there is no remote data stored that would require deletion.
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "10. Children's Privacy",
    content: (
      <>
        <p>
          JustVIN is not designed for or targeted at children under the age of 13. The developer does not knowingly collect, request, or maintain any information from children.
        </p>
      </>
    ),
  },
];

const TERMS_SECTIONS = [
  {
    id: "agreement",
    title: "1. Agreement to Terms",
    content: (
      <>
        <p>
          By downloading, installing, or using <strong>JustVIN</strong> (package name: <code>com.hungryCaterpillar.justvin</code>), you agree to be bound by these Terms of Service.
        </p>
        <p>
          If you do not agree to these terms, do not install or use the application.
        </p>
      </>
    ),
  },
  {
    id: "description",
    title: "2. Description of Service",
    content: (
      <>
        <p>
          JustVIN provides vehicle identification number (VIN) scanning and lookup services using public vehicle specifications data sourced from the National Highway Traffic Safety Administration (NHTSA) vPIC API.
        </p>
        <p>
          Recall lookups are facilitated by launching external web browser links containing the vehicle's VIN. All application data processing occurs locally on your device.
        </p>
      </>
    ),
  },
  {
    id: "warranty",
    title: "3. No Warranty on Data Accuracy",
    content: (
      <>
        <p>
          Vehicle specification details and recall status information are retrieved directly from the NHTSA vPIC API. This data is not verified, audited, or guaranteed by the developer of JustVIN.
        </p>
        <p>
          Use all specifications, recall links, and vehicle data at your own discretion. JustVIN is not liable for any inaccuracies, omissions, errors, or delays in third-party data services.
        </p>
      </>
    ),
  },
  {
    id: "tips",
    title: "4. In-App Tips & Purchases",
    content: (
      <>
        <p>
          JustVIN offers optional, voluntary tip features powered by Google Play Billing.
        </p>
        <p>
          Tips are intended as a direct way to support developer maintenance and do not unlock any additional app features, content, or premium capabilities.
        </p>
        <p>
          All tips are non-refundable except as provided under Google Play's standard refund policies, which are managed independently of the developer.
        </p>
      </>
    ),
  },
  {
    id: "no-accounts",
    title: "5. No Account System",
    content: (
      <>
        <p>
          JustVIN does not require, create, or support user accounts.
        </p>
        <p>
          Because all scan history, bookmarks, and preferences are stored exclusively on your device, this data is unrecoverable if your device is lost, damaged, or if the app is uninstalled.
        </p>
      </>
    ),
  },
  {
    id: "liability",
    title: "6. Limitation of Liability",
    content: (
      <>
        <p>
          JustVIN is provided on an "as is" and "as available" basis without any express or implied warranties.
        </p>
        <p>
          The developer shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising from your use of, or inability to use, the application. This includes reliance on vehicle details, recalls, or mechanical specifications.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "7. Changes to Terms",
    content: (
      <>
        <p>
          These Terms of Service may be updated or modified from time to time.
        </p>
        <p>
          Your continued use of the application following the posting of any changes indicates your acceptance of the revised terms.
        </p>
      </>
    ),
  },
  {
    id: "governing-law",
    title: "8. Governing Law",
    content: (
      <>
        <p>
          These terms and conditions are governed by and construed in accordance with the laws of the <strong>State of Missouri, United States</strong>, without giving effect to any principles of conflicts of law.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "9. Contact Information",
    content: (
      <>
        <p>
          For any questions or inquiries regarding these Terms of Service, please contact Jack Frederick at:
        </p>
        <p style={{ marginTop: "12px" }}>
          <a id="justvin-terms-email" className="privacy-highlight-link" href="mailto:jack@csfrederick.com">
            jack@csfrederick.com
          </a>
        </p>
      </>
    ),
  },
];

const JustVin = () => {
  const initialTab = window.location.pathname.endsWith("/terms") ? "terms" : "privacy";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [activeSection, setActiveSection] = useState("");

  const currentSections = activeTab === "privacy" ? PRIVACY_SECTIONS : TERMS_SECTIONS;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const newPath = tab === "terms" ? "/justvin/terms" : "/justvin/privacy";
    window.history.pushState(null, "", newPath);
  };

  useEffect(() => {
    const handleScroll = () => {
      let current = "";
      for (const section of currentSections) {
        const el = document.getElementById(section.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 180) {
            current = section.id;
          }
        }
      }
      setActiveSection(current || currentSections[0].id);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeTab, currentSections]);

  return (
    <div className="App" style={{ margin: "-8px" }}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(22px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        .privacy-enter-title {
          animation: fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: 0.05s;
        }
        .privacy-enter-subtitle {
          animation: fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: 0.18s;
        }
        .privacy-enter-layout {
          animation: fadeUp 0.85s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: 0.35s;
        }

        /* Glassmorphic Tabs */
        .justvin-tab-container {
          display: inline-flex;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(160, 110, 255, 0.12);
          border-radius: 30px;
          padding: 4px;
          margin-bottom: 32px;
          animation: fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: 0.25s;
        }
        .justvin-tab {
          padding: 10px 24px;
          border-radius: 26px;
          border: none;
          background: transparent;
          color: var(--homeMid);
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
          font-family: "MID";
        }
        .justvin-tab.active {
          background: rgba(160, 110, 255, 0.2);
          color: #fff;
          box-shadow: 0 0 16px rgba(160, 110, 255, 0.2);
          text-shadow: 0 0 8px rgba(255, 255, 255, 0.5);
          border: 1px solid rgba(160, 110, 255, 0.3);
        }
        .justvin-tab:hover:not(.active) {
          color: #fff;
          background: rgba(255, 255, 255, 0.05);
        }

        /* Glassmorphic Cards */
        .privacy-card {
          position: relative;
          border-radius: 14px;
          padding: 24px 28px;
          margin-bottom: 24px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(160, 110, 255, 0.12);
          transition: border-color 0.25s ease,
                      box-shadow 0.25s ease,
                      transform 0.22s cubic-bezier(0.22, 1, 0.36, 1);
          text-align: left;
        }
        .privacy-card:hover {
          border-color: rgba(160, 110, 255, 0.35);
          box-shadow: 0 0 28px 4px rgba(150, 100, 255, 0.1),
                      0 4px 24px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }
        /* Glow behind card on hover */
        .privacy-card::before {
          content: '';
          position: absolute;
          inset: -12px;
          border-radius: 20px;
          background: radial-gradient(ellipse at 50% 50%, rgba(150, 100, 255, 0.08) 0%, transparent 70%);
          filter: blur(12px);
          z-index: -1;
          opacity: 0;
          transition: opacity 0.25s ease;
          pointer-events: none;
        }
        .privacy-card:hover::before {
          opacity: 1;
        }

        /* Layout styles */
        .privacy-container {
          display: flex;
          gap: 40px;
          max-width: 1000px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .privacy-sidebar {
          width: 250px;
          position: sticky;
          top: 100px;
          height: fit-content;
          text-align: left;
        }

        @media (max-width: 800px) {
          .privacy-container {
            flex-direction: column;
          }
          .privacy-sidebar {
            width: 100%;
            position: relative;
            top: 0;
            margin-bottom: 20px;
          }
        }

        .toc-title {
          font-size: 1.1rem;
          color: var(--homeThick);
          margin-bottom: 12px;
          font-weight: bold;
        }

        .toc-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .toc-link {
          color: var(--homeSM);
          text-decoration: none;
          font-size: 0.9rem;
          opacity: 0.6;
          transition: opacity 0.2s ease, color 0.2s ease, padding-left 0.2s ease;
          display: inline-block;
        }
        .toc-link.active {
          opacity: 1;
          color: rgb(170, 130, 255);
          text-shadow: 0 0 8px rgba(170, 130, 255, 0.4);
          padding-left: 6px;
        }
        .toc-link:hover {
          opacity: 0.9;
          color: var(--overLin);
        }

        .privacy-highlight-link {
          color: rgb(180, 140, 255) !important;
          text-shadow: 0 0 10px rgba(180, 140, 255, 0.3);
          transition: opacity 0.2s ease, text-shadow 0.2s ease;
        }
        .privacy-highlight-link:hover {
          opacity: 0.8;
          text-shadow: 0 0 15px rgba(180, 140, 255, 0.6);
        }

        .privacy-content {
          flex: 1;
        }

        .privacy-card-title {
          font-size: 1.3rem;
          color: var(--homeThick);
          margin-top: 0;
          margin-bottom: 16px;
        }
      `}</style>

      <HelmetProvider>
        <Helmet>
          <title>{activeTab === "privacy" ? "JustVIN Privacy Policy" : "JustVIN Terms of Service"} - Jack Frederick</title>
          <meta
            name="JustVIN Legal Info"
            content="Privacy Policy and Terms of Service for the JustVIN mobile app."
            description="Privacy Policy and Terms of Service for the JustVIN mobile app."
            author="Jack Frederick"
          />
        </Helmet>
      </HelmetProvider>

      <Warning />
      <Navbar />

      <header className="Home">
        <br />
        <div className="screenBox Minsize">
          <p className="Homethick privacy-enter-title">JustVIN</p>
          <p className="Homemid privacy-enter-subtitle" style={{ fontSize: "1.1rem", opacity: 0.7 }}>
            Last Updated: July 2, 2026
          </p>
          <br />

          <div className="justvin-tab-container">
            <button
              id="tab-privacy"
              className={`justvin-tab ${activeTab === "privacy" ? "active" : ""}`}
              onClick={() => handleTabChange("privacy")}
            >
              Privacy Policy
            </button>
            <button
              id="tab-terms"
              className={`justvin-tab ${activeTab === "terms" ? "active" : ""}`}
              onClick={() => handleTabChange("terms")}
            >
              Terms of Service
            </button>
          </div>
          <br /><br />

          <div className="privacy-container privacy-enter-layout">
            <nav className="privacy-sidebar" aria-label="Table of Contents">
              <p className="toc-title">Table of Contents</p>
              <ul className="toc-list">
                {currentSections.map((section) => (
                  <li key={section.id}>
                    <a
                      id={`toc-${section.id}`}
                      href={`#${section.id}`}
                      className={`toc-link ${activeSection === section.id ? "active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        const target = document.getElementById(section.id);
                        if (target) {
                          target.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
              <br /><br />
              <a
                id="back-home-button"
                className="Homesmall ats"
                style={{ color: "black", textShadow: "none", display: "inline-block" }}
                href="/"
              >
                Go Back Home
              </a>
            </nav>

            <div className="privacy-content">
              {currentSections.map((section) => (
                <section
                  key={section.id}
                  id={section.id}
                  className="privacy-card"
                >
                  <h2 className="privacy-card-title">{section.title}</h2>
                  <div style={{ color: "var(--homeMid)", lineHeight: "1.6" }}>
                    {section.content}
                  </div>
                </section>
              ))}
            </div>
          </div>

          <br /><br /><br />
          <div className="photoSpacer" />
          <div className="photoSpacer" />
        </div>
      </header>

      <Footer />
    </div>
  );
};

export default JustVin;
